import PropTypes from 'prop-types';
import * as React from 'react';
import warning from 'tiny-warning';
import { DEPRECATED_PROP_MESSAGES } from '../../constants/messages.js';
import { AccordionContext } from './accordion.context.js';
import { deprecatedProp } from '../core/deprecated-prop.js';
import { createComponent } from '../core/create-component.js';
import { SWAN_STYLE_KEY_MAP } from '../head/stylesheets.component.js';
import { useControllableValue } from '../../hooks/use-controllable-value.js';

function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
const SkinEnum = {
    STANDARD: 'standard',
    PEEK: 'peek',
    COLOR_SWATCHES: 'color-swatches'
};
const propTypes = {
    /**
   * The visual style of the Accordion.
   * Available options: "standard", "peek", "color-swatches"
   */ skin: PropTypes.oneOf([
        SkinEnum.STANDARD,
        SkinEnum.PEEK,
        SkinEnum.COLOR_SWATCHES
    ]),
    /**
   * @deprecated omit to use default styling instead
   * Specifies the size
   */ sizeVariant: deprecatedProp(PropTypes.oneOf([
        'standard',
        'large'
    ]), DEPRECATED_PROP_MESSAGES.size),
    /**
   * The size of the accordion
   * @deprecated omit to use default styling instead
   */ size: deprecatedProp(PropTypes.oneOf([
        'standard',
        'large'
    ]), 'Omit to use default styling instead'),
    /**
   * Specifies if the Accordion should have full bleed
   */ fullBleed: PropTypes.bool,
    /**
   * Specifies if only a single collapsible can be expanded at a time or not
   */ single: PropTypes.bool,
    /**
   * The semantic purpose of the Accordion
   * If you want to render a list you can set the  purpose to 'list' and the Accordion will render an <ol> element
   */ purpose: PropTypes.oneOf([
        'list'
    ]),
    /**
   * The map of collapsible IDs and their expanded state.
   */ expandedCollapsibles: PropTypes.objectOf(PropTypes.bool.isRequired),
    /**
   * The callback function for handling expanded change events.
   */ onRequestExpandedChange: PropTypes.func,
    /**
   * The default map of collapsible IDs and their expanded state. \n This is ignored if expandedCollapsibles is provided
   */ defaultExpandedCollapsibles: PropTypes.objectOf(PropTypes.bool.isRequired)
};
const Accordion = createComponent({
    displayName: 'Accordion',
    root: 'div',
    propTypes,
    requiredStyleKeys: SWAN_STYLE_KEY_MAP.accordion,
    propConfig: {
        derivedProps (props) {
            const { single = false, expandedCollapsibles: controlledExpandedCollapsibleMap, onRequestExpandedChange: controlledOnRequestExpandedChange, // if no initial state is provided, start with all collapsible closed
            defaultExpandedCollapsibles: defaultExpandedCollapsibleMap } = props;
            const isControlled = controlledExpandedCollapsibleMap !== undefined;
            warning(// note that we check props.single rather than single; that's because we set a default value on single
            // so we use props.single so that we know if the true value was undefined before the default was applied
            !(isControlled && props.single !== undefined), 'Since `expandedCollapsibles` is provided, `single` will be ignored. The values in `expandedCollapsibles` will be treated as the source of truth');
            const [expandedCollapsibleMap, requestExpandedChange] = useControllableValue({
                value: controlledExpandedCollapsibleMap || undefined,
                onChange: controlledOnRequestExpandedChange || undefined,
                defaultValue: defaultExpandedCollapsibleMap || {},
                defaultOnChange: (setUncontrolledExpandedCollapsibles, ...onChangeArgs)=>{
                    const [collapsibleId, expanded] = onChangeArgs;
                    setUncontrolledExpandedCollapsibles((prevUncontrolledExpandedCollapsibles)=>_object_spread_props(_object_spread({}, !single ? prevUncontrolledExpandedCollapsibles : {}), {
                            [collapsibleId]: expanded
                        }));
                }
            });
            return {
                expandedCollapsibleMap,
                requestExpandedChange
            };
        },
        processProps (props) {
            if (props.purpose === 'list') {
                return _object_spread_props(_object_spread({}, props), {
                    as: props.as || props.component || 'ol'
                });
            }
            return props;
        },
        classNames (props) {
            const { sizeVariant = 'standard', size = sizeVariant, skin, fullBleed } = props;
            const classNameSet = new Set([
                'swan-vanilla-ignore',
                'swan-accordion'
            ]);
            if (size === 'large') classNameSet.add('swan-accordion-large');
            if (skin === 'peek' || skin === 'color-swatches') {
                classNameSet.add(`swan-accordion-skin-${skin}`);
            }
            if (fullBleed) classNameSet.add('swan-accordion-full-bleed');
            return classNameSet;
        },
        wrapRoot (props, derivedProps, element) {
            const { expandedCollapsibleMap, requestExpandedChange } = derivedProps;
            const { purpose } = props;
            return /*#__PURE__*/ React.createElement(AccordionContext.Provider, {
                value: {
                    expandedCollapsibleMap,
                    requestExpandedChange,
                    purpose
                }
            }, element);
        }
    }
});
Accordion.defaultProps = {
    defaultExpandedCollapsibles: {}
};

export { Accordion, SkinEnum };
